import React, { useEffect, useRef } from 'react';
import intlTelInput, { type Iti } from 'intl-tel-input';
import type { SiteChatLang } from '../types';
import { intlLocale, type SiteChatUi } from '../ui';

/**
 * The lead form's phone field: a flag with a country picker and the number formatted as it is typed, the way AXL site
 * forms do it (intl-tel-input, the version `client/site` uses).
 *
 * Only the core is in the bundle. The formatting and validation data (`utils.js`, ~60 KB gzip) and the flag sprites are
 * files next to the embed script (`phone/`, copied by the embed build), loaded when a form with a phone field is on
 * screen. Without them (the admin preview, a blocked file) the field still works: flags come from the admin's own CSS,
 * the number goes as typed and the old digit count checks it.
 */

type ItiOptions = NonNullable<Parameters<typeof intlTelInput>[1]>;
type ItiUtilsModule = Awaited<ReturnType<NonNullable<ItiOptions['loadUtils']>>>;
type ItiCountry = NonNullable<ItiOptions['initialCountry']>;

let assetsBase: string | null = null;
let utilsPromise: Promise<ItiUtilsModule> | null = null;

/** The embed script's folder (`https://static.axl.tech/site-chat/`): where `phone/` lies. */
export function setPhoneAssetsBase(base: string) {
    assetsBase = base.endsWith('/') ? base : `${base}/`;
}

/** The global the embed build's `phone/utils.js` puts the utils on (a classic script: no CORS on a stranger's site). */
export const PHONE_UTILS_GLOBAL = '__axlSiteChatPhoneUtils';

function loadUtils(): Promise<ItiUtilsModule> {
    return utilsPromise ??= new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${phoneFolder(assetsBase!)}utils.js`;
        script.async = true;
        script.onload = () => {
            const utils = (window as unknown as Record<string, ItiUtilsModule['default'] | undefined>)[PHONE_UTILS_GLOBAL];
            if (utils)
                resolve({ default: utils });
            else
                reject(new Error('phone utils'));
        };
        script.onerror = () => {
            utilsPromise = null;
            reject(new Error('phone utils'));
        };
        document.head.appendChild(script);
    });
}

/**
 * `phone/<library version>/` next to the embed script: a new intl-tel-input gets new URLs, so a cached utils file of the
 * old one never meets the new core. The embed build writes the same folder (`phoneAssets` in vite.config.js).
 */
function phoneFolder(base: string) {
    return `${base.endsWith('/') ? base : `${base}/`}phone/${intlTelInput.version}/`;
}

/**
 * The shadow root's copy of the picker's CSS: `:root` matches nothing inside a shadow tree, so its variables go on the
 * host, and the flag sprites point at the files next to the embed script.
 */
export function phoneCss(libraryCss: string, base: string) {
    const folder = phoneFolder(base);
    return `${libraryCss.replace(/:root\s*\{/g, ':host{')}
:host{--iti-path-flags-1x:url("${folder}flags.webp");--iti-path-flags-2x:url("${folder}flags@2x.webp");--iti-path-globe-1x:url("${folder}globe.webp");--iti-path-globe-2x:url("${folder}globe@2x.webp");}`;
}

/**
 * Whether the number is valid for its country (any number type: a landline is a fine lead). Null until the utils are
 * in — the caller falls back to counting digits then.
 */
export function isValidPhone(value: string): boolean | null {
    const utils = intlTelInput.utils;
    if (!utils)
        return null;
    return utils.isValidNumber(value, undefined);
}

/** The visitor's country: the one an AXL site form already found (`ez-country`), else the browser's region, else US. */
function initialCountry(win: Window): ItiCountry {
    try {
        const saved = /(?:^|;\s*)ez-country=([A-Za-z]{2})(?:;|$)/.exec(win.document.cookie)?.[1];
        if (saved)
            return saved.toLowerCase() as ItiCountry;
    } catch {
        // A sandboxed frame: go on with the browser's language.
    }
    for (const tag of win.navigator.languages ?? [win.navigator.language]) {
        const region = /^[a-z]{2,3}[-_]([A-Za-z]{2})\b/.exec(tag ?? '')?.[1];
        if (region)
            return region.toLowerCase() as ItiCountry;
    }
    return 'us';
}

/** Country names in the widget's language (the browser knows them all), and the picker's own words. */
function pickerI18n(lang: SiteChatLang, ui: SiteChatUi) {
    const i18n: Record<string, string> = {
        searchPlaceholder: ui.phoneSearch,
        zeroSearchResults: ui.phoneNoResults,
        oneSearchResult: ui.phoneOneResult,
        multipleSearchResults: ui.phoneResults,
        selectedCountryAriaLabel: ui.phoneCountry,
        noCountrySelected: ui.phoneNoCountry,
        countryListAriaLabel: ui.phoneCountries,
        clearSearchAriaLabel: ui.phoneClearSearch,
    };
    try {
        const names = new Intl.DisplayNames([intlLocale(lang)], { type: 'region' });
        for (const country of intlTelInput.getCountryData()) {
            const name = names.of(country.iso2.toUpperCase());
            if (name && name.toUpperCase() !== country.iso2.toUpperCase())
                i18n[country.iso2] = name;
        }
    } catch {
        // No Intl.DisplayNames: the names stay English.
    }
    return i18n;
}

type PhoneFieldProps = {
    id: string;
    value: string;
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    lang: SiteChatLang;
    ui: SiteChatUi;
    inputRef?: React.Ref<HTMLInputElement>;
    onChange(value: string): void;
};

/**
 * The number goes to the model in E.164 once the utils are in (`+79161234567`), as typed before that. A field holding
 * nothing but a dial code is empty.
 */
export function PhoneField({ id, value, placeholder, disabled, error, lang, ui, inputRef, onChange }: PhoneFieldProps) {
    const input = useRef<HTMLInputElement | null>(null);
    const iti = useRef<Iti | null>(null);
    const emitted = useRef(value);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const el = input.current;
        if (!el)
            return;
        // The live widget's layer: fixed, never transformed, inside the shadow root. The admin preview has none — its
        // field shows the flag but opens no picker.
        const layer = el.closest<HTMLElement>('.axl-sc-layer.fixed');
        const phone = !!el.closest('.axl-sc-layer.phone');
        let instance: Iti | null = null;
        let observer: ResizeObserver | null = null;
        const cleanups: Array<() => void> = [];

        const emit = () => {
            if (!instance)
                return;
            const digits = el.value.replace(/\D/g, '');
            const dialCode = (instance.getSelectedCountryData() as { dialCode?: string }).dialCode ?? '';
            const next = !digits || digits === dialCode ? '' : instance.getNumber() || el.value.trim();
            emitted.current = next;
            onChangeRef.current(next);
        };

        const init = () => {
            instance = intlTelInput(el, {
                allowDropdown: !!layer,
                nationalMode: false,
                autoPlaceholder: placeholder ? 'off' : 'polite',
                formatAsYouType: true,
                initialCountry: initialCountry(window),
                countrySearch: true,
                // Inside the widget's own layer: the default (document.body on a phone) is outside the shadow root,
                // where none of the picker's styles reach.
                dropdownContainer: layer,
                useFullscreenPopup: phone,
                fixDropdownWidth: !phone,
                validationNumberTypes: null,
                i18n: pickerI18n(lang, ui),
                loadUtils: assetsBase ? loadUtils : null,
            });
            iti.current = instance;
            if (value)
                instance.setNumber(value);
            el.addEventListener('input', emit);
            el.addEventListener('countrychange', emit);
            cleanups.push(() => {
                el.removeEventListener('input', emit);
                el.removeEventListener('countrychange', emit);
            });
            if (layer)
                cleanups.push(keepPickerInView(el, layer));
            // The utils reformat what is typed: the model gets the same E.164 the form will send.
            void instance.promise.then(() => el.value && emit(), () => { });
        };

        // The picker measures its flag to pad the input: a field still at zero width (a card mid-animation in a hidden
        // panel) would be measured outside the shadow root, unstyled. It starts once it has a width.
        if (el.offsetWidth > 0)
            init();
        else if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(() => {
                if (el.offsetWidth > 0 && !instance) {
                    observer?.disconnect();
                    init();
                }
            });
            observer.observe(el);
        } else
            init();

        return () => {
            observer?.disconnect();
            cleanups.forEach(cleanup => cleanup());
            instance?.destroy();
            iti.current = null;
        };
        // One picker per field: the language and texts of a running widget do not change under it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A value from outside (the session's lead values, a sign-in) replaces what the field shows.
    useEffect(() => {
        if (iti.current && value !== emitted.current) {
            emitted.current = value;
            iti.current.setNumber(value);
        }
    }, [value]);

    useEffect(() => {
        iti.current?.setDisabled(!!disabled);
    }, [disabled]);

    return (
        <input
            id={id}
            ref={el => {
                input.current = el;
                if (typeof inputRef === 'function')
                    inputRef(el);
                else if (inputRef)
                    (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
            }}
            className={`axl-sc-in ${error ? 'err' : ''}`}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            placeholder={placeholder || undefined}
            defaultValue={value}
            disabled={disabled}
            maxLength={40}
        />
    );
}

/**
 * The picker opens under the field; at the bottom of the panel (where the form usually is) it would hang out of the
 * panel over the site, so it opens above the field when there is room. A click inside the picker must not reach the
 * document: the library closes on any click whose target is outside it, and from the document every click in a shadow
 * tree looks like one on the host.
 */
function keepPickerInView(el: HTMLInputElement, layer: HTMLElement) {
    const stop = (e: Event) => {
        if ((e.target as HTMLElement | null)?.closest?.('.iti__dropdown-content'))
            e.stopPropagation();
    };
    layer.addEventListener('click', stop);

    // Above or below is decided when the picker opens; the search then shrinks the list, and an upward list keeps
    // its bottom at the field instead of leaving a gap.
    let upward = false;
    const place = (opening: boolean) => {
        const dropdown = layer.querySelector<HTMLElement>('.iti--container.iti--inline-dropdown');
        if (!dropdown)
            return;
        const field = el.getBoundingClientRect();
        const panel = el.closest('.axl-sc-pan')?.getBoundingClientRect();
        const bottom = Math.min(window.innerHeight, panel?.bottom ?? window.innerHeight);
        const top = Math.max(0, panel?.top ?? 0);
        // The list itself: it is absolutely positioned, so the fixed container around it has no height of its own.
        const height = dropdown.querySelector<HTMLElement>('.iti__dropdown-content')?.offsetHeight ?? 0;
        // 4 px from the field, less the list's own 3 px top margin from the library CSS.
        const above = field.top - height - 7;
        if (opening)
            upward = field.bottom + height > bottom && above >= top;
        if (upward)
            dropdown.style.top = `${above}px`;
    };
    // The search filters the list a moment after a key (the library debounces it): its new height is watched instead.
    const resized = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => place(false)) : null;
    const onOpen = () => requestAnimationFrame(() => {
        place(true);
        const content = layer.querySelector('.iti--container .iti__dropdown-content');
        if (content && resized) {
            resized.disconnect();
            resized.observe(content);
        }
    });
    el.addEventListener('open:countrydropdown', onOpen);
    return () => {
        layer.removeEventListener('click', stop);
        el.removeEventListener('open:countrydropdown', onOpen);
        resized?.disconnect();
    };
}
